import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import Avatar from '../components/Avatar';
import { 
  Users, Plus, Search, Phone, Mail,
  Calendar, DollarSign, CheckCircle, XCircle, Clock, Shield,
  Briefcase, Home, Wrench, Utensils, Car, Star, UserCheck,
  MoreVertical, Loader2, AlertCircle, X, Copy, Check, Eye, Ban, Pencil, UserX, Trash2, ArrowLeftRight, Send, Link2
} from 'lucide-react';
import api, { condosAPI, employeesAPI, unitsAPI, usersAPI } from '../services/api';
import { isPlanLimitError } from '../services/planLimitModal';
import { formatUnitLabel } from '../utils/unitLabel';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { resolveActiveUnitId } from '../utils/activeUnit';

type StaffRole = 'security' | 'gardener' | 'maid' | 'maintenance' | 'admin' | 'manager' | 'cleaning' | 'chef' | 'concierge' | 'coordinator' | 'guard' | 'admin_staff'
  // Canonical neutral "o/a" forms (Jaime 2026-09-02 19:09) stored in employee_role.
  | 'guardia' | 'mantenimiento' | 'administrativo/a' | 'seguridad' | 'servicio' | 'limpieza' | 'niñero/a' | 'cocinero/a'
  | 'jardinero/a' | 'conserje' | 'gerente' | 'coordinador/a' | 'empleado/a_domestico/a';
// Contract: GET /api/condos/:condoId/people (src/modules/condo/routes.js, spec 2026-08-31)
// ASSUMED CONTRACT (2026-08-31): people rows may include passwordSet/hasPassword
// (false => user has not set a password yet => show re-invite action). Pending alignment pass.
type PeopleEntry = { userId: string; membershipId?: string | null; passwordSet?: boolean | null; fullName: string; roleType: 'propietario' | 'residente' | 'personal'; unitId: string | null; unitLabel: string | null; assignedUnitIds: string[]; staffRole: string | null; email: string | null; photoUrl: string | null; active?: boolean };
type StaffStatus = 'active' | 'inactive' | 'on_leave';

interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: StaffRole;
  status: StaffStatus;
  hourlyRate: number;
  startDate: string;
  rating: number;
  totalJobs: number;
  notes: string;
  relationship?: string;
}

const roleConfig: Record<StaffRole, { label: string; icon: any; color: string }> = {
  security: { label: 'Seguridad', icon: Shield, color: 'bg-blue-500' },
  gardener: { label: 'Jardinero', icon: Home, color: 'bg-green-500' },
  maid: { label: 'Empleada Doméstica', icon: Home, color: 'bg-pink-500' },
  maintenance: { label: 'Mantenimiento', icon: Wrench, color: 'bg-orange-500' },
  admin: { label: 'Administrativo', icon: Briefcase, color: 'bg-purple-500' },
  manager: { label: 'Gerente', icon: UserCheck, color: 'bg-indigo-500' },
  cleaning: { label: 'Limpieza', icon: Home, color: 'bg-cyan-500' },
  chef: { label: 'Chef', icon: Utensils, color: 'bg-red-500' },
  concierge: { label: 'Conserje', icon: Star, color: 'bg-yellow-500' },
  coordinator: { label: 'Coordinador', icon: Calendar, color: 'bg-teal-500' },
  // Backend-normalized employee roles (employees/routes.js): rows created via
  // the Agregar Personal modal store relationship 'guard' / 'admin_staff',
  // which the directory renders through roleConfig — give them real labels
  // instead of falling back to 'Mantenimiento'.
  guard: { label: 'Guardia', icon: Shield, color: 'bg-blue-500' },
  admin_staff: { label: 'Administrativo', icon: Briefcase, color: 'bg-purple-500' },
  // Canonical neutral "o/a" forms (Jaime 2026-09-02 19:09) — labels mirror the dropdown.
  guardia: { label: 'Guardia', icon: Shield, color: 'bg-blue-500' },
  mantenimiento: { label: 'Mantenimiento', icon: Wrench, color: 'bg-orange-500' },
  'administrativo/a': { label: 'Administrativo/a', icon: Briefcase, color: 'bg-purple-500' },
  seguridad: { label: 'Seguridad', icon: Shield, color: 'bg-blue-500' },
  servicio: { label: 'Servicio', icon: Home, color: 'bg-slate-500' },
  limpieza: { label: 'Limpieza', icon: Home, color: 'bg-cyan-500' },
  'niñero/a': { label: 'Niñero/a', icon: UserCheck, color: 'bg-pink-500' },
  'cocinero/a': { label: 'Cocinero/a', icon: Utensils, color: 'bg-red-500' },
  'jardinero/a': { label: 'Jardinero/a', icon: Home, color: 'bg-green-500' },
  conserje: { label: 'Conserje', icon: Star, color: 'bg-yellow-500' },
  gerente: { label: 'Gerente', icon: UserCheck, color: 'bg-indigo-500' },
  'coordinador/a': { label: 'Coordinador/a', icon: Calendar, color: 'bg-teal-500' },
  'empleado/a_domestico/a': { label: 'Empleado/a Doméstico/a', icon: Home, color: 'bg-pink-500' },
};

// CANONICAL (Jaime 2026-09-02 19:09): gender-neutral "o/a" roles for household
// + condo staff. Values are stored verbatim in user_condo_roles.employee_role
// (free-form varchar(50)); backend accepts these plus length-capped free-form.
// Jaime's 8 core roles + historical roles evidenced in
// StaffDirectory.tsx.bak.20260624-012528 (Jardinero/a, Conserje, Gerente, Chef,
// Coordinador/a, Empleado/a Doméstico/a). Labels mirror roleConfig.
const EMPLOYEE_ROLE_OPTIONS = [
  { value: 'guardia', label: 'Guardia' },
  { value: 'mantenimiento', label: 'Mantenimiento' },
  { value: 'administrativo/a', label: 'Administrativo/a' },
  { value: 'seguridad', label: 'Seguridad' },
  { value: 'servicio', label: 'Servicio' },
  { value: 'limpieza', label: 'Limpieza' },
  { value: 'niñero/a', label: 'Niñero/a' },
  { value: 'cocinero/a', label: 'Cocinero/a' },
  { value: 'jardinero/a', label: 'Jardinero/a' },
  { value: 'conserje', label: 'Conserje' },
  { value: 'gerente', label: 'Gerente' },
  { value: 'chef', label: 'Chef' },
  { value: 'coordinador/a', label: 'Coordinador/a' },
  { value: 'empleado/a_domestico/a', label: 'Empleado/a Doméstico/a' },
] as const;

// FIX 2026-08-31 (Jaime): a 400 VALIDATION_ERROR was surfaced as "Internal
// server error" because src/middleware/error.js masks err.message in
// production (error: 'Internal server error') and the old catch-all did
// `setError(err?.response?.data?.error || ...)`, trusting that string.
// Map real backend messages first; only fall back to a generic string for
// network failures / 5xx (or when the body carries no usable detail).
const GENERIC_SERVER_ERROR = /^internal server error$/i;
function apiErrorMessage(err: any, fallback: string): string {
  const status = Number(err?.response?.status || 0);
  const data = err?.response?.data || {};
  const candidates = [
    data.message,
    typeof data.error === 'object' && data.error !== null ? data.error?.message : data.error,
    data.details,
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim() && !GENERIC_SERVER_ERROR.test(c.trim())) return c.trim();
  }
  if (!status || status >= 500) return 'Error del servidor. Inténtalo de nuevo en unos momentos.';
  return fallback;
}

const MENU_W = 224;
const MENU_H = 224;
const computeMenuPos = (r: DOMRect): { left: number; top: number } => {
  let left = r.right - MENU_W;
  left = Math.max(8, Math.min(left, window.innerWidth - MENU_W - 8));
  let top = r.bottom + 4;
  if (top + MENU_H > window.innerHeight - 8) top = Math.max(8, r.top - MENU_H - 4);
  return { left, top };
};

// ASSUMED CONTRACT (2026-08-31): submit shape for existing-user mode is
// { mode:'existing', email|phone, ...extraFields }. Backend child is building
// attach-by-email/phone; contract-alignment pass will confirm.
const identifierIsEmail = (v: string) => v.includes('@');

function UserModeToggle({ value, onChange }: { value: 'new' | 'existing'; onChange: (m: 'new' | 'existing') => void }) {
  const btn = (mode: 'new' | 'existing', label: string) => (
    <button
      key={mode}
      type="button"
      onClick={() => onChange(mode)}
      className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${value === mode ? 'bg-[var(--v-teal-400)] text-white' : 'bg-[var(--v-surface-2)] text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]'}`}
    >
      {label}
    </button>
  );
  return (
    <div>
      <label className="block text-[var(--v-text-secondary)] text-sm mb-1">Tipo de usuario</label>
      <div className="flex gap-1 rounded-lg border border-[var(--v-border-light)] p-1">
        {btn('new', 'Nuevo usuario')}
        {btn('existing', 'Usuario existente')}
      </div>
    </div>
  );
}

export default function StaffDirectory() {
  const { user, contexts, memberships, activeContextId, activeOwnedUnitId, activeCondoId, ownedUnits } = useAuth();
  const location = useLocation();
  // (2026-09-03, Jaime spec) Scope matrix:
  //   /building/staff-directory (Condominio ADMIN lens) -> condo-wide directory
  //     (employeesAPI.getAll + unitsAPI.getAll + GET /condos/:id/people).
  //   /home/staff-directory (Hogar lens) with ANY active unit — owner OR
  //     resident/familiar, standalone OR condo-linked (shared active-unit
  //     resolution, the SAME rule menus/entitlements use) -> unit-scoped
  //     directory. Backend stays authoritative; the client unit filter below
  //     is cosmetic on the rich payload.
  const condoWideLens = location.pathname.startsWith('/building/staff-directory');
  const activeUnitId = resolveActiveUnitId({ activeOwnedUnitId, activeCondoId, contexts, memberships });
  const activeUnit = activeUnitId ? (ownedUnits || []).find((u: any) => String(u.id) === String(activeUnitId)) : undefined;
  // FIX (2026-09-03): when the active unit is a NON-OWNED resident unit, derive
  // its condo linkage from ANY context carrying that unitId (not just kind
  // 'condo'), so the condo-people branch (/condos/:id/people) handles
  // condo-linked resident units instead of falling through to the standalone
  // owned-unit fallback below.
  const fallbackUnitCtx = !activeUnit && activeUnitId
    ? (contexts.find((c) => c.unitId && String(c.unitId) === String(activeUnitId))
      || memberships.find((m) => m.unitId && String(m.unitId) === String(activeUnitId)))
    : undefined;
  const unitCondoId = ((activeUnit as any)?.condo_id ?? fallbackUnitCtx?.condoId ?? undefined) || undefined;
  const unitScopeLabel = formatUnitLabel(activeUnit ?? (fallbackUnitCtx ? { household_name: (fallbackUnitCtx as any).label || (fallbackUnitCtx as any).unitLabel } : undefined), 'tu unidad');
  const unitScoped = !condoWideLens && Boolean(activeUnitId);
  const [staffPerms, setStaffPerms] = useState<{ canSeeColleagues: boolean; canSeeWages: boolean; fromServer: boolean } | null>(null);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState<StaffRole | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<StaffStatus | 'all'>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ left: number; top: number } | null>(null);
  const [expandedStaffId, setExpandedStaffId] = useState<string | null>(null);
  const [copiedEmailId, setCopiedEmailId] = useState<string | null>(null);
  // Employee access-link (Jaime req 2026-08-31): POST /api/employees/:userId/access-link
  // → { inviteUrl }. Shown in a small modal with a Copiar button; link is
  // single-use and expires in 7 days.
  const [accessLink, setAccessLink] = useState<{ url: string; member: StaffMember } | null>(null);
  const [accessLinkCopied, setAccessLinkCopied] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  // Spec 2026-08-31 (Jaime): 'revoke' modal replaced by 'remove' (Eliminar del
  // Personal) confirm; 'Desactivar acceso' needs no dialog (softer action).
  const [modal, setModal] = useState<{ kind: 'edit' | 'remove'; member: StaffMember } | null>(null);
  const [condoUnits, setCondoUnits] = useState<{ id: string; label: string }[]>([]);
  const [extraUnitIds, setExtraUnitIds] = useState<string[]>([]);
  const [wageRate, setWageRate] = useState<'hour' | 'day'>('hour');
  const [editExtras, setEditExtras] = useState<string[]>([]);
  const [editWageAmount, setEditWageAmount] = useState('');
  const [editWageRate, setEditWageRate] = useState<'hour' | 'day'>('hour');
  // Usuarios tabs (Propietarios / Residentes / Personal) + people directory
  const [activeTab, setActiveTab] = useState<'propietarios' | 'residentes' | 'personal'>('personal');
  const [people, setPeople] = useState<PeopleEntry[]>([]);
  const [peopleAvailable, setPeopleAvailable] = useState(true);
  // Selected primary unit for the Agregar Nuevo Personal form (drives the
  // Unidades adicionales list live instead of only the active unit lens).
  const [primaryUnitId, setPrimaryUnitId] = useState<string>(() => (activeOwnedUnitId ? String(activeOwnedUnitId) : ''));
  // Residentes tab: Agregar Residente / Editar residente / Transferir propiedad (Jaime req 2026-08-31)
  const [showAddResidenteModal, setShowAddResidenteModal] = useState(false);
  const [residenteUnitId, setResidenteUnitId] = useState('');
  // EXISTING-USER + INVITE-LINK flows (Jaime req 2026-08-31; ASSUMED CONTRACTS — pending backend alignment pass).
  const [staffUserMode, setStaffUserMode] = useState<'new' | 'existing'>('new');
  const [residentUserMode, setResidentUserMode] = useState<'new' | 'existing'>('new');
  const [invite, setInvite] = useState<{ url: string; kind: 'staff' | 'residente'; joinNotify?: boolean } | null>(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [editPerson, setEditPerson] = useState<PeopleEntry | null>(null);
  const [transferUnit, setTransferUnit] = useState<PeopleEntry | null>(null);
  // Residentes tab (Jaime req 2026-08-31): row menu + "Eliminar de la unidad"
  // confirm. Mirrors the staff card menu treatment.
  const [residentMenu, setResidentMenu] = useState<{ entry: PeopleEntry; pos: { left: number; top: number } } | null>(null);
  const [removeResident, setRemoveResident] = useState<PeopleEntry | null>(null);
  // Menu dismissal (fix 2026-08-31, Jaime bug: ⋮ menu no se cerraba al hacer
  // clic fuera). Both portal menus (staff card + residentes row) share one
  // closer: document mousedown outside the menu portal closes it; clicks on
  // any [data-menu-toggle] button are ignored so the toggle's own onClick
  // still decides open/close (no close-then-reopen flicker); Escape closes;
  // scroll closes because the menus are anchored to fixed viewport coords.
  const menuPortalRef = useRef<HTMLDivElement | null>(null);
  const anyMenuOpen = Boolean(residentMenu) || Boolean(openMenuId);
  useEffect(() => {
    if (!anyMenuOpen) return;
    const close = () => { setResidentMenu(null); setOpenMenuId(null); };
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (menuPortalRef.current && menuPortalRef.current.contains(t)) return;
      if (typeof t.closest === 'function' && t.closest('[data-menu-toggle]')) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    const onScroll = () => close();
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [anyMenuOpen]);
  const getCondoId = () => { const active = contexts.find(c => c.id === activeContextId); const managed = contexts.find(c => c.kind === 'condo'); return active?.condoId || managed?.condoId || user?.condoId; };
  // Staff-permission gating applies only to staff-role viewers; elevated roles
  // (admin/propietario/etc.) always see the full directory.
  const activeRole = contexts.find(c => c.id === activeContextId)?.role || (user as any)?.role || '';
  // People management gating: admin/sub_admin manage the whole condo; propietarios
  // act only on their own unit (unit-scoped actions check activeOwnedUnitId).
  const adminLevel = ['admin', 'super_admin', 'sub_admin'].includes(activeRole);
  // (2026-09-07 21:26 UTC, Jaime ruling — supersedes the 2026-09-03 note):
  // residents may VIEW the directory read-only; only admin/propietario modify
  // (add/edit/deactivate/remove fellow residents). The usuariosOnly
  // non-residente Propietario relies on this page being usable.
  // (2026-09-07, Jaime ruling) A unit's active RESIDENTE can ADD residents/personal
  // (backend POST /units/:id/residents authorizes active resident/familiar members;
  // it rejects propietario creation). Edit/delete/transfer stay admin/propietario-only.
  // 2026-09-07 (Jaime ruling): active residents of their unit get FULL
  // household management of resident-tier members (add + edit + deactivate/
  // remove), minus propietario-only actions. Propietario-only bits
  // (transfer, role changes, adding someone as propietario) stay gated on
  // adminLevel || 'propietario' — see canTransferUnit and the role selects.
  const isResidentActor = activeRole === 'resident' || activeRole === 'residente' || activeRole === 'familiar';
  const actorCanChangeRoles = adminLevel || activeRole === 'propietario';
  const canManageResidentes = adminLevel || activeRole === 'propietario' || isResidentActor;
  const canAddResidente = canManageResidentes;
  const canTransferUnit = (unitId: string | null) => adminLevel || (activeRole === 'propietario' && Boolean(unitId && activeUnitId && String(unitId) === String(activeUnitId)));
  const elevatedViewer = ['admin', 'super_admin', 'sub_admin', 'treasurer', 'propietario', 'resident'].includes(activeRole);
  const staffViewer = !elevatedViewer;
  const canSeeColleagues = !staffViewer || !staffPerms?.fromServer || staffPerms.canSeeColleagues !== false;
  const canSeeWages = !staffViewer || (staffPerms?.fromServer ? staffPerms.canSeeWages === true : true);
  const copyToClipboard = async (value: string, onSuccess?: () => void) => { try { await navigator.clipboard.writeText(value); onSuccess?.(); } catch { setError('No se pudo copiar al portapapeles.'); } };
  // Reenviar enlace de invitación (Jaime add-on 2026-08-31).
  // Contract (backend invites/routes.js): POST /api/invites/resend { userId }
  // → { data: { userId, email, invite: { url, expiresAt, token } } }. 409 ALREADY_SET
  // means the user already set a password and does not need an invite.
  const resendInvite = async (userId: string): Promise<string | null> => {
    try {
      const res = await usersAPI.reinvite(userId);
      const url = res.data?.data?.invite?.url;
      if (url) { setInviteCopied(false); setInvite({ url: String(url), kind: 'residente' }); return String(url); }
      setError('No se pudo obtener el enlace de invitación.');
      return null;
    } catch (err: any) {
      if (err?.response?.status === 409) setError('Este usuario ya estableció su contraseña; no necesita invitación.');
      else if (!isPlanLimitError(err)) setError(apiErrorMessage(err, 'Error al reenviar la invitación'));
      return null;
    }
  };
  // Employee access-link (Jaime req 2026-08-31). Contract: POST
  // /api/employees/:userId/access-link → { inviteUrl } (single-use, 7-day expiry).
  // If the employee has no user account yet, the API returns an error — surface
  // its message instead of a generic string.
  const generateAccessLink = async (member: StaffMember) => {
    const m = member as any;
    const userId = m.userId ?? m.employeeUserId ?? m.employee_user_id;
    if (!userId) { setError('Este empleado aún no tiene cuenta de usuario. Agrega su acceso primero.'); return; }
    setOpenMenuId(null);
    setGeneratingLink(true);
    try {
      const res = await employeesAPI.accessLink(String(userId));
      const url = res.data?.data?.inviteUrl ?? res.data?.inviteUrl;
      if (!url) { setError('No se pudo generar el enlace de acceso.'); return; }
      setAccessLinkCopied(false);
      setAccessLink({ url: String(url), member });
    } catch (err: any) {
      if (!isPlanLimitError(err)) setError(apiErrorMessage(err, 'No se pudo generar el enlace de acceso.'));
    } finally { setGeneratingLink(false); }
  };
  const editStaff = async (member: StaffMember, form: HTMLFormElement) => { const data = new FormData(form); const condoId = getCondoId(); if (!condoId) return; try { await employeesAPI.update(condoId, member.id, { name: String(data.get('name')).trim(), email: String(data.get('email')).trim(), phone: String(data.get('phone') || '').trim() || undefined, additionalUnitIds: editExtras, wageAmount: editWageAmount === '' ? null : Number(editWageAmount), wageRateType: editWageAmount === '' ? null : editWageRate, reachScope: String(data.get('reachScope') || 'all') }); setModal(null); await loadStaff(); } catch { setError('No se pudo actualizar el empleado.'); } };
  // Spec 2026-08-31 (Jaime): two-option staff menu. deactivate = softer amber
  // action (backend: revoke route sets status 'revoked' -> list shows
  // "Inactivo"); remove = red danger action with confirm dialog (ASSUMED
  // DELETE contract, flagged as backend gap); reactivate offered for inactive
  // rows (ASSUMED contract, flagged as backend gap).
  const deactivateStaff = async (member: StaffMember) => { const condoId = getCondoId(); if (!condoId) return; try { await employeesAPI.deactivate(condoId, member.id, 'Desactivado por el administrador'); setOpenMenuId(null); await loadStaff(); } catch { setError('No se pudo desactivar el acceso.'); } };
  const reactivateStaff = async (member: StaffMember) => { const condoId = getCondoId(); if (!condoId) return; try { await employeesAPI.reactivate(condoId, member.id); setOpenMenuId(null); await loadStaff(); } catch (err: any) { setOpenMenuId(null); setError(err?.response?.status === 404 ? 'Reactivar aún no está disponible en el servidor.' : 'No se pudo reactivar el acceso.'); } };
  const removeStaff = async (member: StaffMember) => { const condoId = getCondoId(); if (!condoId) return; try { await employeesAPI.remove(condoId, member.id); setModal(null); setOpenMenuId(null); await loadStaff(); } catch (err: any) { setModal(null); setError(err?.response?.status === 404 ? 'Eliminar del Personal aún no está disponible en el servidor.' : 'No se pudo eliminar al miembro del Personal.'); } };
  // Residentes tab (Jaime req 2026-08-31): mirror of the staff menu.
  // - Eliminar de la unidad (red): DELETE /units/:unitId/residents/:userId
  //   (account kept, removed from the unit only) — confirmed backend route.
  // Contract alignment 2026-08-31: resident deactivate/reactivate keyed by
  // membershipId -> PATCH /condos/:condoId/memberships/:membershipId/{deactivate,reactivate}.
  const deactivateResident = async (p: PeopleEntry) => { const condoId = getCondoId(); if (!condoId || !p.membershipId) return; try { await condosAPI.deactivatePerson(condoId, p.membershipId, 'Desactivado por el administrador'); setResidentMenu(null); await loadStaff(); } catch { setError('No se pudo desactivar el acceso.'); } };
  const reactivateResident = async (p: PeopleEntry) => { const condoId = getCondoId(); if (!condoId || !p.membershipId) return; try { await condosAPI.reactivatePerson(condoId, p.membershipId); setResidentMenu(null); await loadStaff(); } catch { setError('No se pudo reactivar el acceso.'); } };
  const removeResidentFromUnit = async (p: PeopleEntry) => { if (!p.unitId || !p.userId) return; try { await condosAPI.removeResidentFromUnit(String(p.unitId), p.userId); setRemoveResident(null); setResidentMenu(null); await loadStaff(); } catch (err: any) { setRemoveResident(null); setError(err?.response?.status === 404 ? 'Eliminar de la unidad aún no está disponible en el servidor.' : 'No se pudo eliminar al residente de la unidad.'); } };
  // (2026-09-03, Jaime) "Agregarme como residente": a non-residente Propietario
  // adds THEIR OWN resident membership via POST /units/:id/residents with their
  // own user id (backend contract: body { userId, role: 'resident' } — direct
  // userId attach, no invite). Hidden once they already appear as residente.
  const [selfAddingResident, setSelfAddingResident] = useState(false);
  const addSelfAsResidente = async () => {
    if (!activeUnitId || !user?.id) return;
    setSelfAddingResident(true);
    try {
      await unitsAPI.addResidentToUnit(String(activeUnitId), { userId: user.id, role: 'resident' });
      setNotice('Te agregaste como residente de esta unidad.');
      await loadStaff();
    } catch (err: any) {
      setError(apiErrorMessage(err, 'No se pudo agregarte como residente de la unidad.'));
    } finally { setSelfAddingResident(false); }
  };

  const loadStaff = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const active = contexts.find(c => c.id === activeContextId);
      // FIX (2026-09-03, Jaime bug): after a full page refresh with a standalone
      // unit active ("casa prueba · Sin vincular"), this page showed the
      // condo-wide directory of a DIFFERENT condo (TH1 / Buena Vista). Root
      // cause: the old derivation
      // `active?.condoId || managed?.condoId || user?.condoId` fell back to ANY
      // managed condo context (here: the viewer's employee membership in TH1)
      // whenever the active context carried no condoId, so the condo-wide
      // branch ran instead of the unit-scoped one. The other home-lens tabs
      // derive from the ACTIVE UNIT's condo linkage (LensDashboard: unit.condo_id),
      // not from a condo fallback — mirror that here: when the active context is
      // the viewer's HOME (personal context, NOT the condo context itself), the
      // directory is unit-scoped to the active unit (standalone OR condo-linked).
      // A condo admin whose ACTIVE context is the condo keeps the condo-wide view.
      const condoId = condoWideLens ? (active?.condoId || user?.condoId) : undefined;
      if (condoWideLens && condoId) {
        // Ruling 2026-09-09 (Jaime): the condo-wide lens shows the FULL
        // directory (condo-level + unit-linked staff); backend default is now
        // condo-only, so request scope=all explicitly.
        const res = await employeesAPI.getAll(condoId, 'all');
        // Units are fetched independently so a units-endpoint failure can no
        // longer blank the staff list AND the unit selectors at the same time.
        let units: any[] = [];
        try {
          const unitsRes = await unitsAPI.getAll(condoId);
          const unitData = unitsRes.data?.data ?? unitsRes.data;
          units = Array.isArray(unitData) ? unitData : unitData?.units || unitData?.items || [];
        } catch { units = []; }
        // Staff visibility permissions (backend contract in flight). If the
        // endpoint 404s/errors, fall back to current behavior (all visible).
        try {
          const permsRes = await employeesAPI.permissions(condoId);
          const permsData = permsRes.data?.data ?? permsRes.data;
          if (permsData && typeof permsData === 'object' && !Array.isArray(permsData)) {
            setStaffPerms({ canSeeColleagues: permsData.canSeeColleagues !== false, canSeeWages: permsData.canSeeWages === true, fromServer: true });
          }
        } catch {
          setStaffPerms(null);
        }
        const data = res.data?.data ?? res.data;
        // Merge condo units with the user's own units so the staff form always
        // offers every unit available to the user in this condo context.
        const owned = (ownedUnits || []).filter((u: any) => String(u.condo_id ?? (u as any).condoId ?? '') === String(condoId));
        const merged = [...units];
        for (const ou of owned) {
          if (!merged.some((u: any) => String(u.id) === String(ou.id))) merged.push(ou);
        }
        // Always include the viewer's active owned unit, even if the owned-units
        // list has not fully hydrated yet (fixes owned unit missing from
        // Unidades adicionales).
        if (activeOwnedUnitId && !merged.some((u: any) => String(u.id) === String(activeOwnedUnitId))) {
          const activeOwned = (ownedUnits || []).find((u: any) => String(u.id) === String(activeOwnedUnitId));
          if (activeOwned) merged.push(activeOwned);
        }
        // Shared label helper: prefer unit name (TH1 / Allegra 601), fall back to number.
        const unitMap = new Map(merged.map((u: any) => [String(u.id), formatUnitLabel(u, '')]));
        setCondoUnits(merged.map((u: any) => ({ id: String(u.id), label: formatUnitLabel(u, 'Unidad') })));
        // People directory (GET /condos/:condoId/people). If it 404s/errors we
        // degrade gracefully: Personal tab keeps the existing staff list.
        try {
          const peopleRes = await condosAPI.people(condoId);
          const pdata = peopleRes.data?.data ?? peopleRes.data;
          const plist = Array.isArray(pdata) ? pdata : pdata?.people || [];
          setPeople(plist.map((p: any) => ({
            userId: String(p.userId ?? p.user_id ?? ''),
            membershipId: p.membershipId ?? p.membership_id ?? null,
            passwordSet: (p.passwordSet ?? p.hasPassword ?? null) as boolean | null,
            fullName: String(p.fullName ?? p.full_name ?? '').trim(),
            roleType: (p.roleType === 'propietario' ? 'propietario' : (p.roleType === 'personal' ? 'personal' : 'residente')) as PeopleEntry['roleType'],
            unitId: p.unitId ?? p.unit_id ?? null,
            unitLabel: p.unitLabel ?? p.unit_label ?? null,
            assignedUnitIds: Array.isArray(p.assignedUnitIds) ? p.assignedUnitIds.map(String) : [],
            staffRole: p.staffRole ?? null,
            email: p.email ?? null,
            photoUrl: p.photoUrl ?? null,
            // GAP: people endpoint only lists active=1 memberships, so inactive
            // (suspended) residents disappear entirely. When the backend adds an
            // active/status field, rows map here for the "Inactivo" treatment.
            active: p.active !== false && p.status !== 'inactive',
          })));
          setPeopleAvailable(true);
        } catch { setPeopleAvailable(false); }
        const staffList = Array.isArray(data) ? data : data?.employees || data?.items || [];
        setStaff(staffList.map((member: any) => {
          const names = String(member.name || '').trim().split(/\s+/);
          const isEmployee = Boolean(member.employeeUserId || member.employee_user_id || member.relationship);
          return { ...member, firstName: member.firstName || names[0] || '', lastName: member.lastName || names.slice(1).join(' '), role: member.role || member.relationship || 'maintenance', status: member.status === 'revoked' ? 'inactive' : (member.status || 'active'), rating: Number(member.rating || 0), hourlyRate: Number(member.hourlyRate || 0), totalJobs: Number(member.totalJobs || 0), ...(isEmployee ? { unitLabel: member.unitLabel || unitMap.get(member.unitId), isEmployee: true } : { unitLabel: member.unitLabel || unitMap.get(member.unitId) || 'Sin unidad' }) };
        }));
      } else {
        // (2026-09-03, Jaime spec) Unit-scoped view. People come from the rich
        // unit-members payload; Personal rows are mapped onto StaffMember so
        // the existing Personal tab (filters/stats/menus) renders unchanged.
        const applyUnitScope = (entries: PeopleEntry[]) => {
          setPeople(entries);
          setPeopleAvailable(true);
          setStaff(entries.filter((e) => e.roleType === 'personal').map((e) => {
            const names = String(e.fullName || '').trim().split(/\s+/);
            return {
              id: String(e.membershipId || e.userId),
              firstName: names[0] || '',
              lastName: names.slice(1).join(' '),
              email: e.email || '',
              phone: '',
              role: ((e.staffRole as StaffRole) || 'maintenance'),
              status: (e.active === false ? 'inactive' : 'active') as StaffStatus,
              hourlyRate: 0,
              startDate: '',
              rating: 0,
              totalJobs: 0,
              notes: '',
              relationship: e.staffRole || undefined,
              unitLabel: e.unitLabel || undefined,
              isEmployee: true,
              userId: e.userId,
            } as any as StaffMember;
          }));
        };
        if (activeUnitId && unitCondoId) {
          // Condo-linked unit: GET /condos/:condoId/people is authoritative and
          // already scopes rows to the viewer's own unit memberships
          // (propietario / resident / familiar, staff assignments included),
          // returning avatar_url, role/sub_type/employee_role and staffRole.
          try {
            const peopleRes = await condosAPI.people(String(unitCondoId));
            const pdata = peopleRes.data?.data ?? peopleRes.data;
            const plist = Array.isArray(pdata) ? pdata : pdata?.people || [];
            const mapped = plist.map((p: any) => ({
              userId: String(p.userId ?? p.user_id ?? ''),
              membershipId: p.membershipId ?? p.membership_id ?? null,
              passwordSet: (p.passwordSet ?? p.hasPassword ?? null) as boolean | null,
              fullName: String(p.fullName ?? p.full_name ?? '').trim(),
              roleType: (p.roleType === 'propietario' ? 'propietario' : (p.roleType === 'personal' ? 'personal' : 'residente')) as PeopleEntry['roleType'],
              unitId: p.unitId ?? p.unit_id ?? null,
              unitLabel: p.unitLabel ?? p.unit_label ?? null,
              assignedUnitIds: Array.isArray(p.assignedUnitIds) ? p.assignedUnitIds.map(String) : [],
              staffRole: p.staffRole ?? null,
              email: p.email ?? null,
              photoUrl: p.photoUrl ?? null,
              active: p.active !== false && p.status !== 'inactive',
            }));
            // Cosmetic client filter to the ACTIVE unit (backend already scopes).
            applyUnitScope(mapped.filter((p: PeopleEntry) => String(p.unitId) === String(activeUnitId) || p.assignedUnitIds.includes(String(activeUnitId))));
          } catch {
            setPeople([]);
            setStaff([]);
            setPeopleAvailable(false);
          }
        } else {
        setStaff([]);
        // FIX 2026-08-31 (Usuarios, Bug A - Jaime): a STANDALONE unit (no condo)
        // has no condoId, so the condo people endpoint above never runs and the
        // Propietarios/Residentes tabs showed "Sin ... por ahora" even though
        // the unit has an owner/residents in the DB. Use GET /units/:id which
        // returns unit.residents (active user_condo_roles rows joined to users)
        // and map them into the same PeopleEntry shape.
        const standaloneCandidate = activeUnit
          || (ownedUnits || []).find((u: any) => String(u.id) === String(activeOwnedUnitId) && !u.condo_id)
          || ((ownedUnits || []).length === 1 && !(ownedUnits || [])[0]?.condo_id ? (ownedUnits || [])[0] : undefined);
        // FIX (2026-09-03, wrong-directory bug): the standalone fallback applies
        // ONLY when there is no active unit OR the active unit IS this
        // standalone unit. If the active unit is a different (condo-linked)
        // unit, the condo-people branch above (unitCondoId derived from its
        // context) is authoritative; otherwise the user's single owned
        // standalone unit leaked into every other unit's directory.
        const standaloneUnit = standaloneCandidate?.id && (!activeUnitId || String(activeUnitId) === String(standaloneCandidate.id))
          ? standaloneCandidate
          : undefined;
        if (standaloneUnit?.id) {
          try {
            const unitRes = await unitsAPI.getResidents(String(standaloneUnit.id));
            const udata = unitRes.data?.data ?? unitRes.data;
            const unit = udata?.unit ?? udata;
            const residents: any[] = Array.isArray(unit?.residents) ? unit.residents : [];
            const unitLabel = formatUnitLabel(unit ?? standaloneUnit, 'Unidad');
            applyUnitScope(residents.map((r: any) => ({
              userId: String(r.user_id ?? r.userId ?? ''),
              membershipId: r.id ?? r.membership_id ?? null,
              passwordSet: null as boolean | null,
              fullName: String(r.name ?? '').trim(),
              // HOTFIX (2026-09-03, role-labeling regression): mirror the backend
              // condo-people mapping (api/src/modules/condo/routes.js): propietario
              // -> 'propietario', empleado (or employee markers) -> 'personal',
              // everything else ('resident'/'familiar'/'inquilino') -> 'residente'.
              roleType: (String(r.role) === 'propietario' || String(r.role) === 'owner' || r.sub_type === 'propietario'
                ? 'propietario'
                : (String(r.role) === 'empleado' || r.sub_type === 'empleado' || r.employee_role || r.staff_relationship)
                  ? 'personal'
                  : 'residente') as PeopleEntry['roleType'],
              unitId: String(standaloneUnit.id),
              unitLabel,
              assignedUnitIds: [String(standaloneUnit.id)],
              staffRole: r.employee_role ?? null,
              email: r.email ?? null,
              photoUrl: (r.avatar_url ?? null) as string | null,
              active: true,
            })));
          } catch {
            setPeople([]);
            setStaff([]);
            setPeopleAvailable(false);
          }
        } else {
          setPeople([]);
          setStaff([]);
          setPeopleAvailable(true);
        }
        }
      }
    } catch (err: any) {
      console.error('Error loading staff:', err);
      if (err?.response?.status !== 401) {
        setError('No se pudo cargar el personal. Intenta más tarde.');
      }
      setStaff([]);
    } finally {
      setLoading(false);
    }
  }, [user?.condoId, contexts, activeContextId, ownedUnits, activeOwnedUnitId, memberships, activeCondoId, activeUnitId, unitCondoId, condoWideLens]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const filteredStaff = staff.filter(member => {
    const fullName = `${member.firstName || ''} ${member.lastName || ''}`.toLowerCase();
    const matchesSearch = fullName.includes(searchQuery.toLowerCase()) ||
                         (member.email || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === 'all' || member.role === filterRole;
    const matchesStatus = filterStatus === 'all' || member.status === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const stats = {
    total: staff.length,
    active: staff.filter(s => s.status === 'active').length,
    onLeave: staff.filter(s => s.status === 'on_leave').length,
    avgRating: staff.length > 0 ? (staff.reduce((acc, s) => acc + (s.rating || 0), 0) / staff.length).toFixed(1) : '—',
  };

  const getStatusBadge = (status: StaffStatus) => {
    const styles: Record<string, string> = {
      active: 'bg-emerald-500/10 text-emerald-400',
      inactive: 'bg-slate-500/10 text-[var(--v-text-secondary)]',
      on_leave: 'bg-amber-500/10 text-amber-400',
    };
    const labels: Record<string, string> = {
      active: 'Activo',
      inactive: 'Inactivo',
      on_leave: 'En Licencia',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status] || styles.inactive}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">{unitScoped ? `Usuarios · ${unitScopeLabel}` : 'Usuarios'}</h2>
          <p className="text-[var(--v-text-secondary)]">{unitScoped ? `Propietarios, residentes y personal de ${unitScopeLabel}` : 'Propietarios, residentes y personal del condominio'}</p>
        </div>
        {activeTab === 'personal' && (
        <button 
          onClick={() => { setExtraUnitIds([]); setWageRate('hour'); setShowAddModal(true); }}
          className="bg-[var(--v-teal-400)] hover:bg-[var(--v-teal-500)] text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Agregar Personal
        </button>
        )}
        {activeTab === 'residentes' && canAddResidente && (
        <button
          onClick={() => { setResidenteUnitId(activeUnitId ? String(activeUnitId) : ''); setShowAddResidenteModal(true); }}
          className="bg-[var(--v-teal-400)] hover:bg-[var(--v-teal-500)] text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Agregar Residente
        </button>
        )}
        {(activeTab === 'residentes' || activeTab === 'propietarios') && (() => {
          // (2026-09-03, Jaime) Non-residente Propietario self-add. Shown only
          // when the viewer owns the active unit, is not admin-level, and is NOT
          // already listed as a residente; disappears once they are residente.
          if (adminLevel || activeRole !== 'propietario' || !activeUnitId || selfAddingResident) return null;
          const isSelfResidente = people.some((entry) => entry.userId === user?.id && entry.roleType === 'residente');
          if (isSelfResidente) return null;
          return (
            <button
              onClick={() => { void addSelfAsResidente(); }}
              disabled={selfAddingResident}
              className="border border-[var(--v-teal-400)] text-[var(--v-teal-400)] hover:bg-[var(--v-teal-400)]/10 px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors disabled:opacity-60"
            >
              {selfAddingResident ? <Loader2 className="w-5 h-5 animate-spin" /> : <UserCheck className="w-5 h-5" />}
              Agregarme como residente
            </button>
          );
        })()}
      </div>

      {/* Tabs: Propietarios / Residentes / Personal */}
      <div className="flex gap-1 border-b border-[var(--v-border-light)] overflow-x-auto">
        {([['propietarios', 'Propietarios'], ['residentes', 'Residentes'], ['personal', 'Personal']] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${activeTab === key ? 'border-[var(--v-teal-400)] text-[var(--v-teal-400)]' : 'border-transparent text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Propietarios / Residentes directory (people endpoint) */}
      {!loading && (activeTab === 'propietarios' || activeTab === 'residentes') && (() => {
        const list = people.filter(p => (activeTab === 'propietarios' ? p.roleType === 'propietario' : p.roleType === 'residente'));
        const plural = activeTab === 'propietarios' ? 'propietarios' : 'residentes';
        if (!peopleAvailable) return (
          <div className="bg-[var(--v-surface-1)] p-6 rounded-xl border border-[var(--v-border-light)] text-center text-sm text-[var(--v-text-secondary)]">
            El directorio de personas no está disponible en este momento.
          </div>
        );
        if (!list.length) return (
          <div className="bg-[var(--v-surface-1)] p-10 rounded-xl border border-[var(--v-border-light)] text-center">
            <Users className="w-12 h-12 text-[var(--v-text-tertiary)] mx-auto mb-3 opacity-50" />
            <h3 className="text-lg font-semibold text-[var(--v-text-primary)] mb-1">Sin {plural} por ahora</h3>
            <p className="text-[var(--v-text-secondary)] text-sm">Cuando haya {plural} en tu unidad o condominio, aparecerán aquí.</p>
          </div>
        );
        return (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {list.map(p => (
              <div key={p.membershipId || p.userId} className={`bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] p-4 flex items-center gap-3 ${p.active === false ? 'opacity-60 grayscale-[30%]' : ''}`}>
                {/* FEATURE (2026-09-01, Jaime ruling): shared <Avatar> (photo via photoUrl). */}
                <Avatar name={p.fullName} avatarUrl={p.photoUrl} size={40} alt={p.fullName} />
                <div className="min-w-0 flex-1">
                  <h4 className="text-[var(--v-text-primary)] font-medium truncate">{p.fullName || 'Sin nombre'}{p.active === false && <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-500/20 text-gray-300 align-middle">Inactivo</span>}</h4>
                  <p className="text-[var(--v-text-secondary)] text-sm truncate">{p.unitLabel || 'Sin unidad'}{p.email ? ` · ${p.email}` : ''}</p>
                </div>
                {((activeTab === 'propietarios' && canTransferUnit(p.unitId)) || (activeTab === 'residentes' && canManageResidentes && p.membershipId)) && (
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    {activeTab === 'propietarios' && canTransferUnit(p.unitId) && (
                      <button onClick={() => setTransferUnit(p)} title="Transferir propiedad" aria-label="Transferir propiedad" className="p-1.5 text-[var(--v-text-secondary)] hover:text-[var(--v-teal-400)] hover:bg-[var(--v-surface-2)] rounded-lg"><ArrowLeftRight className="w-4 h-4" /></button>
                    )}
                    {activeTab === 'residentes' && canManageResidentes && p.membershipId && p.passwordSet === false && (
                      <button onClick={() => void resendInvite(p.userId)} title="Reenviar enlace de invitación" aria-label="Reenviar enlace de invitación" className="p-1.5 text-[var(--v-text-secondary)] hover:text-[var(--v-teal-400)] hover:bg-[var(--v-surface-2)] rounded-lg"><Send className="w-4 h-4" /></button>
                    )}
                    {activeTab === 'residentes' && canManageResidentes && p.membershipId && (
                      <button onClick={() => setEditPerson(p)} title="Editar" aria-label="Editar residente" className="p-1.5 text-[var(--v-text-secondary)] hover:text-[var(--v-teal-400)] hover:bg-[var(--v-surface-2)] rounded-lg"><Pencil className="w-4 h-4" /></button>
                    )}
                    {activeTab === 'residentes' && canManageResidentes && p.membershipId && (
                      <button data-menu-toggle onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { const r = (e.currentTarget as HTMLElement).getBoundingClientRect(); setResidentMenu((prev) => (prev?.entry === p ? null : { entry: p, pos: computeMenuPos(r) })); }} title="Más acciones" aria-label="Más acciones" className="p-1.5 text-[var(--v-text-secondary)] hover:text-[var(--v-teal-400)] hover:bg-[var(--v-surface-2)] rounded-lg"><MoreVertical className="w-4 h-4" /></button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })()}

      {/* Success notice */}
      {notice && (
        <div className="bg-[var(--v-surface-2)] border border-[var(--v-teal-400)]/30 rounded-lg p-3 flex items-center gap-2 text-[var(--v-teal-400)]">
          <span className="text-sm">{notice}</span>
          <button onClick={() => setNotice(null)} className="ml-auto text-xs opacity-70 hover:opacity-100">✕</button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-[var(--v-danger-bg)] border border-[var(--v-danger)]/30 rounded-lg p-3 flex items-center gap-2 text-[var(--v-danger)]">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-[var(--v-text-secondary)]">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Cargando personal...
        </div>
      )}

      {/* Empty State */}
      {!loading && activeTab === 'personal' && staff.length === 0 && !error && (
        <div className="bg-[var(--v-surface-1)] p-12 rounded-xl border border-[var(--v-border-light)] text-center">
          <Users className="w-16 h-16 text-[var(--v-text-tertiary)] mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold text-[var(--v-text-primary)] mb-2">No tienes personal registrado</h3>
          <p className="text-[var(--v-text-secondary)] mb-4">
            Agrega personal de servicio, seguridad, mantenimiento o limpieza para gestionarlos aquí.
          </p>
          <button
            onClick={() => { setExtraUnitIds([]); setWageRate('hour'); setShowAddModal(true); }}
            className="px-4 py-2 bg-[var(--v-teal-400)] text-white font-bold rounded-xl hover:bg-[var(--v-teal-500)] transition-colors"
          >
            <Plus className="w-4 h-4 inline mr-1" /> Agregar Personal
          </button>
        </div>
      )}

      {/* Staff viewers without can_see_colleagues: hide the directory entirely */}
      {!loading && activeTab === 'personal' && !canSeeColleagues && (
        <div className="bg-[var(--v-surface-1)] p-12 rounded-xl border border-[var(--v-border-light)] text-center">
          <Shield className="w-16 h-16 text-[var(--v-text-tertiary)] mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold text-[var(--v-text-primary)] mb-2">Sin acceso al personal</h3>
          <p className="text-[var(--v-text-secondary)]">No tienes permiso para ver el resto del personal.</p>
        </div>
      )}

      {/* Stats + List (only when staff exists) */}
      {!loading && activeTab === 'personal' && staff.length > 0 && canSeeColleagues && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-border-light)]">
              <p className="text-[var(--v-text-secondary)] text-sm">Total Personal</p>
              <p className="text-3xl font-bold text-[var(--v-text-primary)] mt-1">{stats.total}</p>
            </div>
            <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-border-light)]">
              <p className="text-[var(--v-text-secondary)] text-sm">Activos</p>
              <p className="text-3xl font-bold text-emerald-400 mt-1">{stats.active}</p>
            </div>
            <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-border-light)]">
              <p className="text-[var(--v-text-secondary)] text-sm">En Licencia</p>
              <p className="text-3xl font-bold text-amber-400 mt-1">{stats.onLeave}</p>
            </div>
            <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-border-light)]">
              <p className="text-[var(--v-text-secondary)] text-sm">Calificación Promedio</p>
              <p className="text-3xl font-bold text-purple-400 mt-1">{stats.avgRating}★</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--v-text-tertiary)]" />
              <input
                type="text"
                placeholder="Buscar personal..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[var(--v-surface-1)] text-[var(--v-text-primary)] pl-10 pr-4 py-2 rounded-lg border border-[var(--v-border-light)] focus:border-[var(--v-teal-400)] focus:outline-none"
              />
            </div>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value as StaffRole | 'all')}
              className="bg-[var(--v-surface-1)] text-[var(--v-text-primary)] px-4 py-2 rounded-lg border border-[var(--v-border-light)]"
            >
              <option value="all">Todos los roles</option>
              {Object.entries(roleConfig).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as StaffStatus | 'all')}
              className="bg-[var(--v-surface-1)] text-[var(--v-text-primary)] px-4 py-2 rounded-lg border border-[var(--v-border-light)]"
            >
              <option value="all">Todos los estados</option>
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
              <option value="on_leave">En Licencia</option>
            </select>
          </div>

          {/* Staff List */}
          <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] overflow-hidden">
            <div className="divide-y divide-[var(--v-border-light)]">
              {filteredStaff.map((member) => {
                const role = roleConfig[member.role] || roleConfig.maintenance;
                const RoleIcon = role.icon;
                return (
                  <div
                    key={member.id}
                    className={`p-4 hover:bg-[var(--v-surface-2)] cursor-pointer transition ${member.status === 'inactive' ? 'opacity-60 grayscale-[30%]' : ''}`}
                  >
                    <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 ${role.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <RoleIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <h4 className="text-[var(--v-text-primary)] font-semibold truncate">{member.firstName} {member.lastName}</h4>
                        {getStatusBadge(member.status)}
                      </div>
                      <p className="text-[var(--v-text-secondary)] text-sm">{role.label}{(member as any).isEmployee && member.relationship ? ` · ${member.relationship}` : (member as any).unitLabel ? ` · ${(member as any).unitLabel}` : ''}</p>
                      <div className="flex flex-wrap items-center gap-4 mt-1 text-xs text-[var(--v-text-tertiary)]">
                        {member.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {member.phone}
                          </span>
                        )}
                        {canSeeWages && (((member as any).wageAmount ?? 0) > 0) && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-3 h-3" />
                            ${(member as any).wageAmount}{(member as any).wageRateType === 'day' ? '/día' : '/h'}
                          </span>
                        )}
                        {member.rating > 0 && (
                          <span className="flex items-center gap-1">
                            <Star className="w-3 h-3 text-amber-400" />
                            {member.rating} ({member.totalJobs || 0} trabajos)
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="relative flex-shrink-0"><button data-menu-toggle aria-label={`Acciones de ${member.firstName} ${member.lastName}`} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setMenuPos(computeMenuPos(r)); setOpenMenuId(openMenuId === member.id ? null : member.id); }} className="p-2 text-[var(--v-text-primary)] hover:bg-[var(--v-surface-2)] rounded-lg"><MoreVertical className="w-5 h-5" /></button></div>
                    </div>
                  {expandedStaffId === member.id && (
                    <div className="mt-3 pl-16 pr-2 border-t border-[var(--v-border-light)] pt-3 text-sm text-[var(--v-text-secondary)]">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                        <div className="flex gap-2 min-w-0">
                          <Mail className="w-4 h-4 flex-shrink-0 mt-0.5 text-[var(--v-text-tertiary)]" />
                          <span className="min-w-0">
                            <span className="text-[var(--v-text-tertiary)]">Email: </span>
                            <span className="text-[var(--v-text-primary)] break-all">{member.email || '—'}</span>
                            {copiedEmailId === member.id && <Check className="inline w-3 h-3 ml-1 text-emerald-400" />}
                          </span>
                        </div>
                        <div className="flex gap-2 min-w-0">
                          <Phone className="w-4 h-4 flex-shrink-0 mt-0.5 text-[var(--v-text-tertiary)]" />
                          <span className="min-w-0">
                            <span className="text-[var(--v-text-tertiary)]">Teléfono: </span>
                            <span className="text-[var(--v-text-primary)] break-all">{member.phone || '—'}</span>
                          </span>
                        </div>
                        {(() => { const m = member as any; if (m.passwordSet === false) return (<div className="flex gap-2 min-w-0 items-center"><Clock className="w-4 h-4 flex-shrink-0 text-amber-400" /><span className="text-amber-400">Pendiente de activación</span></div>); if (m.userId || m.employeeUserId || m.employee_user_id) return (<div className="flex gap-2 min-w-0 items-center"><CheckCircle className="w-4 h-4 flex-shrink-0 text-emerald-400" /><span className="text-emerald-400">Tiene acceso</span></div>); return null; })()}
                        {!(member as any).isEmployee && (
                          <>
                            <div className="flex gap-2 min-w-0">
                              <Home className="w-4 h-4 flex-shrink-0 mt-0.5 text-[var(--v-text-tertiary)]" />
                              <span className="min-w-0">
                                <span className="text-[var(--v-text-tertiary)]">Unidad: </span>
                                <span className="text-[var(--v-text-primary)] break-all">{(member as any).unitLabel || 'Sin unidad'}</span>
                              </span>
                            </div>
                            <div className="flex gap-2 min-w-0">
                              <Calendar className="w-4 h-4 flex-shrink-0 mt-0.5 text-[var(--v-text-tertiary)]" />
                              <span className="min-w-0">
                                <span className="text-[var(--v-text-tertiary)]">Inicio: </span>
                                <span className="text-[var(--v-text-primary)] break-all">{member.startDate || '—'}</span>
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  </div>
                );
              })}
            </div>
          </div>

          {filteredStaff.length === 0 && (
            <div className="text-center py-8 text-[var(--v-text-tertiary)]">
              <p>No se encontró personal con los filtros seleccionados</p>
            </div>
          )}
        </>
      )}

      {modal?.kind === 'edit' && <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"><div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] w-full max-w-md p-6 shadow-2xl"><div className="flex items-center justify-between mb-4"><h3 className="text-xl font-semibold text-[var(--v-text-primary)]">Editar datos</h3><button aria-label="Cerrar" onClick={() => setModal(null)}><X className="w-5 h-5" /></button></div><form className="space-y-4" onSubmit={(e) => { e.preventDefault(); void editStaff(modal.member, e.currentTarget); }}><label className="block text-sm text-[var(--v-text-secondary)]">Nombre completo<input name="name" required defaultValue={`${modal.member.firstName} ${modal.member.lastName}`.trim()} className="mt-1 w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-3 py-2 rounded-lg border border-[var(--v-border-light)]" /></label><label className="block text-sm text-[var(--v-text-secondary)]">Email<input name="email" type="email" required defaultValue={modal.member.email} className="mt-1 w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-3 py-2 rounded-lg border border-[var(--v-border-light)]" /></label><label className="block text-sm text-[var(--v-text-secondary)]">Teléfono<input name="phone" defaultValue={modal.member.phone} className="mt-1 w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-3 py-2 rounded-lg border border-[var(--v-border-light)]" /></label><div><label className="block text-sm text-[var(--v-text-secondary)] mb-1">Tarifa ($) — {editWageRate === 'day' ? 'Por día' : 'Por hora'}</label><div className="flex gap-2"><input type="number" min="0" step="0.01" value={editWageAmount} onChange={(e) => setEditWageAmount(e.target.value)} placeholder="0.00" className="w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-3 py-2 rounded-lg border border-[var(--v-border-light)]" /><div className="flex rounded-lg border border-[var(--v-border-light)] overflow-hidden flex-shrink-0"><button type="button" onClick={() => setEditWageRate('hour')} className={`px-3 text-xs font-medium transition-colors ${editWageRate === 'hour' ? 'bg-[var(--v-teal-400)] text-white' : 'bg-[var(--v-surface-2)] text-[var(--v-text-secondary)]'}`}>Por hora</button><button type="button" onClick={() => setEditWageRate('day')} className={`px-3 text-xs font-medium transition-colors ${editWageRate === 'day' ? 'bg-[var(--v-teal-400)] text-white' : 'bg-[var(--v-surface-2)] text-[var(--v-text-secondary)]'}`}>Por día</button></div></div></div><label className="block text-sm text-[var(--v-text-secondary)]">Puede ser contactado por<select name="reachScope" defaultValue={(modal.member as any).reachScope === 'admin_staff' ? 'admin_staff' : 'all'} className="mt-1 w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-3 py-2 rounded-lg border border-[var(--v-border-light)]"><option value="all">Todos</option><option value="admin_staff">Solo administradores y empleados</option></select></label>{(() => { const m = modal.member as any; const primaryId = String(m.primaryUnitId ?? m.unitId ?? ''); const options = condoUnits.filter(u => u.id !== primaryId); if (options.length === 0) return null; return (<div><label className="block text-sm text-[var(--v-text-secondary)] mb-1">Unidades adicionales</label><div className="flex flex-wrap gap-2">{options.map(u => { const checked = editExtras.includes(u.id); return (<label key={u.id} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm cursor-pointer transition-colors ${checked ? 'bg-[var(--v-teal-400)]/15 border-[var(--v-teal-400)] text-[var(--v-teal-400)] font-medium' : 'bg-[var(--v-surface-2)] border-[var(--v-border-light)] text-[var(--v-text-secondary)] hover:border-[var(--v-teal-400)]/50'}`}><input type="checkbox" className="accent-[var(--v-teal-400)]" checked={checked} onChange={() => setEditExtras(prev => checked ? prev.filter(id => id !== u.id) : [...prev, u.id])} />{u.label}</label>); })}</div></div>); })()}<div className="flex gap-3 pt-2"><button type="button" onClick={() => setModal(null)} className="flex-1 py-2 rounded-lg text-[var(--v-text-secondary)]">Cancelar</button><button type="submit" className="flex-1 py-2 rounded-lg bg-[var(--v-teal-400)] text-white font-medium">Guardar</button></div></form></div></div>}
      {modal?.kind === 'remove' && <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"><div className="bg-[var(--v-surface-1)] rounded-xl border border-red-400/40 w-full max-w-md p-6 shadow-2xl"><h3 className="text-xl font-semibold text-[var(--v-text-primary)]">Eliminar del Personal</h3><p className="text-sm text-[var(--v-text-secondary)] mt-2">Se eliminará a <strong className="text-[var(--v-text-primary)]">{modal.member.firstName} {modal.member.lastName}</strong> de tu lista de Personal. Esto lo quitará de tu lista de Personal. Su cuenta de Vecindus no se elimina.</p><div className="flex gap-3 mt-6"><button type="button" onClick={() => setModal(null)} className="flex-1 py-2 rounded-lg text-[var(--v-text-secondary)]">Cancelar</button><button type="button" onClick={() => { void removeStaff(modal.member); }} className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium">Eliminar</button></div></div></div>}

      {/* Residentes menu (Jaime req 2026-08-31): Desactivar acceso / Eliminar de la unidad / Reactivar */}
      {residentMenu && createPortal(
        <div ref={menuPortalRef} className="fixed z-50 w-56 rounded-xl border border-[var(--v-border-light)] bg-[var(--v-surface-1)] py-2 shadow-2xl" style={{ left: residentMenu.pos.left, top: residentMenu.pos.top }}>
          {residentMenu.entry.active !== false && residentMenu.entry.userId && <button onClick={() => { const uid = String(residentMenu.entry.userId); setResidentMenu(null); void resendInvite(uid); }} className="w-full px-4 py-2.5 text-left text-sm text-[var(--v-text-primary)] hover:bg-[var(--v-surface-2)] flex items-center gap-3"><Send className="w-4 h-4" />Reenviar enlace</button>}
          {/* FIX (2026-09-07, review): Desactivar/Reactivar are condo-membership
              routes (PATCH /condos/:id/members/:membershipId/deactivate) that
              backend does NOT open to resident actors — hide them for
              residentes (they remove via the unit DELETE path, which now
              supports standalone too); admins/propietarios keep them. */}
          {(adminLevel || activeRole === 'propietario') && residentMenu.entry.active !== false && <button onClick={() => { setResidentMenu(null); void deactivateResident(residentMenu.entry); }} className="w-full px-4 py-2.5 text-left text-sm text-amber-300 hover:bg-amber-500/10 flex items-center gap-3"><UserX className="w-4 h-4" />Desactivar acceso</button>}
          {residentMenu.entry.active !== false && <button onClick={() => { setRemoveResident(residentMenu.entry); setResidentMenu(null); }} className="w-full px-4 py-2.5 text-left text-sm text-red-300 hover:bg-red-500/10 flex items-center gap-3"><Trash2 className="w-4 h-4" />Eliminar de la unidad</button>}
          {(adminLevel || activeRole === 'propietario') && residentMenu.entry.active === false && <button onClick={() => { const p = residentMenu.entry; setResidentMenu(null); void reactivateResident(p); }} className="w-full px-4 py-2.5 text-left text-sm text-[var(--v-teal-400)] hover:bg-[var(--v-teal-400)]/10 flex items-center gap-3"><UserCheck className="w-4 h-4" />Reactivar</button>}
        </div>, document.body)}

      {removeResident && <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"><div className="bg-[var(--v-surface-1)] rounded-xl border border-red-400/40 w-full max-w-md p-6 shadow-2xl"><h3 className="text-xl font-semibold text-[var(--v-text-primary)]">Eliminar de la unidad</h3><p className="text-sm text-[var(--v-text-secondary)] mt-2">Se eliminará a <strong className="text-[var(--v-text-primary)]">{removeResident.fullName}</strong> de la unidad {removeResident.unitLabel || 'asignada'}. Su cuenta de Vecindus no se elimina; solo pierde el acceso como residente de esta unidad.</p><div className="flex gap-3 mt-6"><button type="button" onClick={() => setRemoveResident(null)} className="flex-1 py-2 rounded-lg text-[var(--v-text-secondary)]">Cancelar</button><button type="button" onClick={() => { void removeResidentFromUnit(removeResident); }} className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium">Eliminar</button></div></div></div>}

      {openMenuId && menuPos && (() => { const member = staff.find((m) => m.id === openMenuId); if (!member) return null; return createPortal(
        <div ref={menuPortalRef} className="fixed z-50 w-56 rounded-xl border border-[var(--v-border-light)] bg-[var(--v-surface-1)] py-2 shadow-2xl" style={{ left: menuPos.left, top: menuPos.top }}>
          <button onClick={() => { setExpandedStaffId(expandedStaffId === member.id ? null : member.id); setOpenMenuId(null); }} className="w-full px-4 py-2.5 text-left text-sm text-[var(--v-text-primary)] hover:bg-[var(--v-surface-2)] flex items-center gap-3"><Eye className="w-4 h-4" />Ver detalles</button>
          <button onClick={() => { copyToClipboard(member.email, () => setCopiedEmailId(member.id)); setOpenMenuId(null); }} disabled={!member.email} className="w-full px-4 py-2.5 text-left text-sm text-[var(--v-text-primary)] hover:bg-[var(--v-surface-2)] flex items-center gap-3 disabled:opacity-40"><Copy className="w-4 h-4" />Copiar email</button>
          <button onClick={() => { setOpenMenuId(null); const m = member as any; const primaryId = String(m.primaryUnitId ?? m.unitId ?? ''); setEditExtras(((m.unitIds || []) as string[]).map(String).filter(u => u !== primaryId)); setEditWageAmount(m.wageAmount != null ? String(m.wageAmount) : ''); setEditWageRate(m.wageRateType === 'day' ? 'day' : 'hour'); setModal({ kind: 'edit', member }); }} className="w-full px-4 py-2.5 text-left text-sm text-[var(--v-text-primary)] hover:bg-[var(--v-surface-2)] flex items-center gap-3"><Pencil className="w-4 h-4" />Editar datos</button>
          {(member as any).userId && (
            <button onClick={() => { setOpenMenuId(null); void resendInvite(String((member as any).userId)); }} className="w-full px-4 py-2.5 text-left text-sm text-[var(--v-text-primary)] hover:bg-[var(--v-surface-2)] flex items-center gap-3"><Send className="w-4 h-4" />Reenviar enlace</button>
          )}
          {member.status !== 'inactive' && <button onClick={() => { void generateAccessLink(member); }} disabled={generatingLink} className="w-full px-4 py-2.5 text-left text-sm text-[var(--v-text-primary)] hover:bg-[var(--v-surface-2)] flex items-center gap-3 disabled:opacity-40"><Link2 className="w-4 h-4" />{generatingLink ? 'Generando…' : 'Generar enlace de acceso'}</button>}
          {member.status !== 'inactive' && <button onClick={() => { setOpenMenuId(null); void deactivateStaff(member); }} className="w-full px-4 py-2.5 text-left text-sm text-amber-300 hover:bg-amber-500/10 flex items-center gap-3"><UserX className="w-4 h-4" />Desactivar acceso</button>}
          {member.status !== 'inactive' && <button onClick={() => { setOpenMenuId(null); setModal({ kind: 'remove', member }); }} className="w-full px-4 py-2.5 text-left text-sm text-red-300 hover:bg-red-500/10 flex items-center gap-3"><Trash2 className="w-4 h-4" />Eliminar del Personal</button>}
          {member.status === 'inactive' && <button onClick={() => { setOpenMenuId(null); void reactivateStaff(member); }} className="w-full px-4 py-2.5 text-left text-sm text-[var(--v-teal-400)] hover:bg-[var(--v-teal-400)]/10 flex items-center gap-3"><UserCheck className="w-4 h-4" />Reactivar</button>}
        </div>, document.body); })()}

      {/* Invite-link popup (Jaime req 2026-08-31): shown when creation returns { inviteUrl }. */}
      {invite && (() => {
        // join_notify (existing-user attach): the person already has an
        // account — the link lands on the accept page with a "go to login"
        // outcome, so the copy points them to sign in instead of creating
        // a password.
        const msg = invite.joinNotify
          ? `Ya tienes acceso a esta unidad. Entra con tu cuenta: ${invite.url}`
          : `Te invito a Vecindus. Crea tu contraseña con este enlace: ${invite.url}`;
        const mailSubject = invite.joinNotify ? 'Acceso a Vecindus' : 'Invitación a Vecindus';
        return (
          <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] w-full max-w-md p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-[var(--v-text-primary)]">{invite.joinNotify ? 'Usuario vinculado' : 'Usuario creado'}</h3>
                <button aria-label="Cerrar" onClick={() => setInvite(null)}><X className="w-5 h-5" /></button>
              </div>
              <p className="text-[var(--v-text-secondary)] text-sm mb-3">{invite.joinNotify ? 'Ya tiene acceso a esta unidad. Envíale este enlace para que inicie sesión con su cuenta.' : 'Envía este enlace para que cree su contraseña.'}</p>
              <div className="flex items-center gap-2 rounded-lg bg-[var(--v-surface-2)] border border-[var(--v-border-light)] p-3">
                <code className="flex-1 text-xs break-all text-[var(--v-teal-400)]">{invite.url}</code>
                <button
                  onClick={async () => { await copyToClipboard(invite.url, () => setInviteCopied(true)); }}
                  aria-label="Copiar enlace"
                  className="flex items-center gap-1 text-xs font-medium text-[var(--v-text-primary)] hover:text-[var(--v-teal-400)] flex-shrink-0"
                >
                  {inviteCopied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  {inviteCopied ? 'Copiado' : 'Copiar enlace'}
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4">
                <a href={`https://wa.me/?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 py-2 rounded-lg border border-[var(--v-border-light)] text-sm text-[var(--v-text-primary)] hover:border-[var(--v-teal-400)] transition-colors"><Phone className="w-4 h-4" />WhatsApp</a>
                <a href={`sms:?&body=${encodeURIComponent(msg)}`} className="flex items-center justify-center gap-1.5 py-2 rounded-lg border border-[var(--v-border-light)] text-sm text-[var(--v-text-primary)] hover:border-[var(--v-teal-400)] transition-colors"><Phone className="w-4 h-4" />SMS</a>
                <a href={`mailto:?subject=${encodeURIComponent(mailSubject)}&body=${encodeURIComponent(msg)}`} className="flex items-center justify-center gap-1.5 py-2 rounded-lg border border-[var(--v-border-light)] text-sm text-[var(--v-text-primary)] hover:border-[var(--v-teal-400)] transition-colors"><Mail className="w-4 h-4" />Email</a>
              </div>
              <button onClick={() => setInvite(null)} className="mt-5 w-full py-2 rounded-lg bg-[var(--v-teal-400)] text-white font-medium">Listo</button>
            </div>
          </div>
        );
      })()}

      {/* Employee access-link modal (Jaime req 2026-08-31): single-use link, 7-day expiry. */}
      {accessLink && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-[var(--v-text-primary)]">Enlace de acceso</h3>
              <button aria-label="Cerrar" onClick={() => setAccessLink(null)}><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-[var(--v-text-secondary)] mb-3">Comparte este enlace con <strong className="text-[var(--v-text-primary)]">{accessLink.member.firstName} {accessLink.member.lastName}</strong> para que active su acceso a Vecindus.</p>
            <div className="flex items-center gap-2 rounded-lg bg-[var(--v-surface-2)] border border-[var(--v-border-light)] p-3">
              <code className="flex-1 text-sm text-[var(--v-teal-400)] break-all">{accessLink.url}</code>
              <button onClick={() => void copyToClipboard(accessLink.url, () => setAccessLinkCopied(true))} aria-label="Copiar enlace" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--v-teal-400)] text-white text-sm font-medium flex-shrink-0">
                {accessLinkCopied ? <><Check className="w-4 h-4" />Copiado</> : <><Copy className="w-4 h-4" />Copiar</>}
              </button>
            </div>
            <p className="text-xs text-[var(--v-text-tertiary)] mt-3">Este enlace es de un solo uso y caduca en 7 días.</p>
            <button onClick={() => setAccessLink(null)} className="mt-5 w-full py-2 rounded-lg bg-[var(--v-teal-400)] text-white font-medium">Listo</button>
          </div>
        </div>
      )}
      {tempPassword && <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true"><div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] w-full max-w-md p-6 shadow-2xl"><div className="flex items-center justify-between mb-4"><h3 className="text-xl font-semibold text-[var(--v-text-primary)]">Personal agregado</h3><button aria-label="Cerrar" onClick={() => setTempPassword(null)}><X className="w-5 h-5" /></button></div><p className="text-[var(--v-text-secondary)] text-sm mb-3">Comparte esta contraseña temporal con el empleado. Deberá cambiarla en su primer inicio de sesión.</p><div className="flex items-center gap-2 rounded-lg bg-[var(--v-surface-2)] border border-[var(--v-border-light)] p-3"><code className="flex-1 text-2xl font-bold tracking-wider text-[var(--v-teal-400)]">{tempPassword}</code><button onClick={() => copyToClipboard(tempPassword, () => setCopiedEmailId('password'))} aria-label="Copiar contraseña">{copiedEmailId === 'password' ? <Check className="w-5 h-5 text-emerald-400" /> : <Copy className="w-5 h-5" />}</button></div><button onClick={() => setTempPassword(null)} className="mt-5 w-full py-2 rounded-lg bg-[var(--v-teal-400)] text-white font-medium">Listo</button></div></div>}

      {/* Add Staff Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-[var(--v-border-light)] flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[var(--v-text-primary)]">Agregar Nuevo Personal</h3>
              <button onClick={() => setShowAddModal(false)} className="text-[var(--v-text-tertiary)] hover:text-[var(--v-text-primary)]">✕</button>
            </div>
            <form className="p-4 space-y-4" onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              try {
                const active = contexts.find(c => c.id === activeContextId);
                const managed = contexts.find(c => c.kind === 'condo');
                const condoId = active?.condoId || managed?.condoId || user?.condoId;
                if (!condoId) throw new Error('No se encontró el condominio activo');
                // FIX 2026-08-31: <select name="role"> now only offers
                // EMPLOYEE_ROLE_OPTIONS whose values are exactly the canonical
                // backend employeeRole values — no legacy mapping needed.
                const employeeRole = String(formData.get('role') || '');
                const wageRaw = String(formData.get('wageAmount') || '').trim();
                const primaryUnitId = String(formData.get('unitId') || '').trim();
                const shared = {
                  employeeRole,
                  ...(primaryUnitId ? { unitId: primaryUnitId } : {}),
                  additionalUnitIds: extraUnitIds.filter(u => u !== primaryUnitId),
                  ...(wageRaw ? { wageAmount: Number(wageRaw), wageRateType: wageRate } : {}),
                  // Staff visibility flags (backend column support landing in parallel).
                  can_see_colleagues: formData.get('can_see_colleagues') != null,
                  can_see_wages: formData.get('can_see_wages') != null,
                  // Reachability (2026-09-18, Jaime): who may contact this
                  // employee. 'all' default; 'admin_staff' = only condo
                  // admins + fellow staff.
                  reachScope: String(formData.get('reachScope') || 'all'),
                };
                // ASSUMED CONTRACT (2026-08-31): existing-user mode sends
                // { mode:'existing', email|phone, ... }; 404 => unknown user.
                const payload: any = staffUserMode === 'existing'
                  ? (() => { const ident = String(formData.get('identifier') || '').trim(); return { mode: 'existing', ...(identifierIsEmail(ident) ? { email: ident } : { phone: ident }), ...shared }; })()
                  : {
                      name: `${formData.get('firstName')} ${formData.get('lastName')}`,
                      email: formData.get('email'),
                      phone: formData.get('phone'),
                      // Invite-link flow is primary: no creator-entered password.
                      generateInvite: true,
                      ...shared,
                    };
                const response = await employeesAPI.create(condoId, payload);
                const created = response.data?.data?.employee;
                setShowAddModal(false);
                // Contract (backend employees/routes.js): new-user flow with
                // generateInvite returns employee.invite.url (single-use link).
                // Absent invite => fall back to backend-generated tempPassword.
                // Invite contract: new-user flow returns employee.invite.url;
                // existing-user (attach) flow returns data.invite.url with
                // type 'join_notify' (backend landing in parallel). If an
                // existing-mode attach returns no invite (older contract),
                // fall through silently — no popup, no error.
                const inv = response.data?.data?.employee?.invite || response.data?.data?.invite || created?.invite;
                const inviteUrl = inv?.url;
                if (inviteUrl) { setInviteCopied(false); setInvite({ url: String(inviteUrl), kind: 'staff', joinNotify: staffUserMode === 'existing' || inv?.type === 'join_notify' }); }
                else if (created?.tempPassword) setTempPassword(created.tempPassword);
                await loadStaff();
              } catch (err: any) {
                // Plan-limit 403s are surfaced by the global PlanLimitModal
                // (api.ts interceptor) — skip the duplicate inline error.
                if (err?.response?.status === 404) {
                  setError('No existe un usuario con ese correo/teléfono. Cambia a "Nuevo usuario" para crearlo.');
                } else if (!isPlanLimitError(err)) {
                  setError(apiErrorMessage(err, 'Error al agregar personal. Verifica los datos e inténtalo de nuevo.'));
                }
              }
            }}>
              <UserModeToggle value={staffUserMode} onChange={setStaffUserMode} />
              {staffUserMode === 'new' ? (
                <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[var(--v-text-secondary)] text-sm mb-1">Nombre</label>
                  <input type="text" name="firstName" required className="w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-4 py-2 rounded-lg border border-[var(--v-border-light)]" />
                </div>
                <div>
                  <label className="block text-[var(--v-text-secondary)] text-sm mb-1">Apellido</label>
                  <input type="text" name="lastName" required className="w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-4 py-2 rounded-lg border border-[var(--v-border-light)]" />
                </div>
              </div>
              <div>
                <label className="block text-[var(--v-text-secondary)] text-sm mb-1">Email</label>
                <input type="email" name="email" required className="w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-4 py-2 rounded-lg border border-[var(--v-border-light)]" />
              </div>
              <div>
                <label className="block text-[var(--v-text-secondary)] text-sm mb-1">Teléfono</label>
                <input type="tel" name="phone" className="w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-4 py-2 rounded-lg border border-[var(--v-border-light)]" />
              </div>
                </>
              ) : (
                <div>
                  <label className="block text-[var(--v-text-secondary)] text-sm mb-1">Correo o teléfono del usuario existente</label>
                  <input type="text" name="identifier" required placeholder="usuario@correo.com o +52..." className="w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-4 py-2 rounded-lg border border-[var(--v-border-light)]" />
                  <p className="mt-1 text-xs text-[var(--v-text-tertiary)]">El usuario ya debe tener una cuenta en Vecindus. Si no la tiene, usa "Nuevo usuario".</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[var(--v-text-secondary)] text-sm mb-1">Rol</label>
                  <select name="role" className="w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-4 py-2 rounded-lg border border-[var(--v-border-light)]">
                    {EMPLOYEE_ROLE_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[var(--v-text-secondary)] text-sm mb-1">Tarifa ($) — {wageRate === 'day' ? 'Por día' : 'Por hora'}</label>
                  <div className="flex gap-2">
                    <input type="number" name="wageAmount" min="0" step="0.01" defaultValue="" placeholder="0.00" className="w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-4 py-2 rounded-lg border border-[var(--v-border-light)]" />
                    <div className="flex rounded-lg border border-[var(--v-border-light)] overflow-hidden flex-shrink-0">
                      <button type="button" onClick={() => setWageRate('hour')} className={`px-3 text-xs font-medium transition-colors ${wageRate === 'hour' ? 'bg-[var(--v-teal-400)] text-white' : 'bg-[var(--v-surface-2)] text-[var(--v-text-secondary)]'}`}>Por hora</button>
                      <button type="button" onClick={() => setWageRate('day')} className={`px-3 text-xs font-medium transition-colors ${wageRate === 'day' ? 'bg-[var(--v-teal-400)] text-white' : 'bg-[var(--v-surface-2)] text-[var(--v-text-secondary)]'}`}>Por día</button>
                    </div>
                  </div>
                </div>
              </div>
              {(condoUnits.length > 0 || activeOwnedUnitId) && (() => {
                const primaryId = primaryUnitId;
                const activeOwned = (ownedUnits || []).find((u: any) => String(u.id) === String(activeOwnedUnitId));
                const missing = Boolean(primaryId) && !condoUnits.some(u => u.id === primaryId);
                return (
                  <div>
                    <label className="block text-[var(--v-text-secondary)] text-sm mb-1">Unidad principal</label>
                    <select name="unitId" value={primaryId} onChange={(e) => { const id = e.target.value; setPrimaryUnitId(id); setExtraUnitIds(prev => prev.filter(u => u !== id)); }} className="w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-4 py-2 rounded-lg border border-[var(--v-border-light)]">
                      {!primaryId && <option value="">Selecciona unidad</option>}
                      {condoUnits.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}
                      {missing && <option value={primaryId}>{formatUnitLabel(activeOwned, 'Unidad activa')}</option>}
                    </select>
                  </div>
                );
              })()}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-[var(--v-text-secondary)]">
                  <input type="checkbox" name="can_see_colleagues" defaultChecked className="accent-[var(--v-teal-400)]" />
                  Puede ver al resto del personal
                </label>
                <label className="flex items-center gap-2 text-sm text-[var(--v-text-secondary)]">
                  <input type="checkbox" name="can_see_wages" className="accent-[var(--v-teal-400)]" />
                  Puede ver los sueldos/salarios
                </label>
                <label className="block text-sm text-[var(--v-text-secondary)]">
                  Puede ser contactado por
                  <select name="reachScope" defaultValue="all" className="mt-1 w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-3 py-2 rounded-lg border border-[var(--v-border-light)]">
                    <option value="all">Todos</option>
                    <option value="admin_staff">Solo administradores y empleados</option>
                  </select>
                </label>
              </div>
              {condoUnits.filter(u => u.id !== primaryUnitId).length > 0 && (
                <div>
                  <label className="block text-[var(--v-text-secondary)] text-sm mb-1">Unidades adicionales</label>
                  <div className="flex flex-wrap gap-2">
                    {condoUnits.filter(u => u.id !== primaryUnitId).map(u => {
                      const checked = extraUnitIds.includes(u.id);
                      return (
                        <label key={u.id} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm cursor-pointer transition-colors ${checked ? 'bg-[var(--v-teal-400)]/15 border-[var(--v-teal-400)] text-[var(--v-teal-400)] font-medium' : 'bg-[var(--v-surface-2)] border-[var(--v-border-light)] text-[var(--v-text-secondary)] hover:border-[var(--v-teal-400)]/50'}`}>
                          <input type="checkbox" className="accent-[var(--v-teal-400)]" checked={checked} onChange={() => setExtraUnitIds(prev => checked ? prev.filter(id => id !== u.id) : [...prev, u.id])} />
                          {u.label}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="p-4 border-t border-[var(--v-border-light)] flex gap-3">
                <button type="button" onClick={() => setShowAddModal(false)} className="flex-1 py-2 rounded-lg text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]">
                  Cancelar
                </button>
                <button type="submit" className="flex-1 bg-[var(--v-teal-400)] hover:bg-[var(--v-teal-500)] text-white py-2 rounded-lg font-medium transition-colors">
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Agregar Residente modal — POST /condos/:condoId/members. New user: { name, email, password?, unitId, role } (no password => invite-link flow); existing user: { mode:'existing', email|phone, unitId, role }. ASSUMED CONTRACTS pending alignment pass. */}
      {showAddResidenteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-[var(--v-border-light)] flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[var(--v-text-primary)]">Agregar Residente</h3>
              <button onClick={() => setShowAddResidenteModal(false)} className="text-[var(--v-text-tertiary)] hover:text-[var(--v-text-primary)]">✕</button>
            </div>
            <form className="p-4 space-y-4" onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              const active = contexts.find(c => c.id === activeContextId);
              const managed = contexts.find(c => c.kind === 'condo');
              // (2026-09-03) unitCondoId: the ACTIVE UNIT's condo linkage so a
              // Hogar-lens owner/resident of a condo-linked unit can resolve the
              // condo even when the active context carries no condoId.
              const condoId = active?.condoId || managed?.condoId || user?.condoId || unitCondoId;
              // UNIFIED (2026-09-07, Jaime ruling): ONE rule set for standalone
              // AND condo-linked units. Whenever the target unit is known
              // (standalone OR condo-linked), add via POST /units/:unitId/residents
              // (unitsAPI.addResidentToUnit); backend unit routes authorize
              // owner/propietario/condo-admin/residente-actor uniformly. The
              // condos endpoint stays ONLY for flows with no unit anchor
              // (legacy condo rows without a unit). Previously this branched on
              // ownUnitScoped (own active unit, non-admin) and !condoId, sending
              // condo-linked adds to POST /condos/:id/members and 403s for
              // non-admin actors.
              const targetUnitId = residenteUnitId || (activeUnitId ? String(activeUnitId) : '');
              const unitScopedUnitId = targetUnitId || undefined;
              // Renamed 2026-09-03: no longer shadows the component-level
              // `unitScoped` view-scope flag.
              const unitScopedSubmit = !!unitScopedUnitId;
              if (!condoId && !unitScopedSubmit) { setError('No se encontró el condominio activo'); return; }
              try {
                // ASSUMED CONTRACT (2026-08-31): mode 'existing' => { mode:'existing', email|phone, ... },
                // 404 => unknown user; new mode without password triggers invite flow,
                // response may include { inviteUrl }. Pending alignment pass.
                // (2026-09-07) 'personal' form option maps to the backend's
                // 'empleado' role (valid for both POST /units/:id/residents and
                // POST /condos/:id/members); residents adders never see 'propietario'.
                const rawRole = String(formData.get('role') || 'resident');
                const role = rawRole === 'propietario' ? 'propietario' : (rawRole === 'personal' ? 'empleado' : 'resident');
                const payload: any = residentUserMode === 'existing'
                  ? (() => { const ident = String(formData.get('identifier') || '').trim(); return { mode: 'existing', ...(identifierIsEmail(ident) ? { email: ident } : { phone: ident }), unitId: residenteUnitId || undefined, role }; })()
                  : {
                      name: String(formData.get('name') || '').trim(),
                      email: String(formData.get('email') || '').trim(),
                      // Contract (backend condo/routes.js POST /:id/members): new-user
                      // mode with generateInvite:true and NO password creates a
                      // passwordless user and returns data.invite.url (single-use link).
                      generateInvite: true,
                      unitId: residenteUnitId || undefined,
                      role,
                    };
                // Unit-keyed path (any unit with an anchor): 'propietario' is not a
                // valid body role for POST /units/:id/residents (the caller IS
                // the owner), so it is downgraded to 'resident'.
                const unitPayload = unitScopedSubmit && role === 'propietario' ? { ...payload, role: 'resident' } : payload;
                const response = unitScopedSubmit
                  ? await unitsAPI.addResidentToUnit(String(unitScopedUnitId as string), unitPayload)
                  : await condosAPI.createResident(condoId as string, payload);
                setShowAddResidenteModal(false);
                const inv = response.data?.data?.invite;
                if (inv?.url) { setInviteCopied(false); setInvite({ url: String(inv.url), kind: 'residente', joinNotify: residentUserMode === 'existing' || inv?.type === 'join_notify' }); }
                else if (residentUserMode === 'new') setError('Residente creado, pero no se recibió el enlace de invitación.');
                // Existing-user mode without invite (older contract): silent success, no popup.
                await loadStaff();
              } catch (err: any) {
                // Plan-limit 403s are surfaced by the global PlanLimitModal interceptor.
                if (err?.response?.status === 404) {
                  setError('No existe un usuario con ese correo/teléfono. Cambia a "Nuevo usuario" para crearlo.');
                } else if (!isPlanLimitError(err)) {
                  setError(apiErrorMessage(err, 'Error al agregar residente'));
                }
              }
            }}>
              <UserModeToggle value={residentUserMode} onChange={setResidentUserMode} />
              {residentUserMode === 'new' ? (
                <>
              <div>
                <label className="block text-[var(--v-text-secondary)] text-sm mb-1">Nombre completo</label>
                <input type="text" name="name" required className="w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-4 py-2 rounded-lg border border-[var(--v-border-light)]" />
              </div>
              <div>
                <label className="block text-[var(--v-text-secondary)] text-sm mb-1">Email</label>
                <input type="email" name="email" required className="w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-4 py-2 rounded-lg border border-[var(--v-border-light)]" />
              </div>
                </>
              ) : (
                <div>
                  <label className="block text-[var(--v-text-secondary)] text-sm mb-1">Correo o teléfono del usuario existente</label>
                  <input type="text" name="identifier" required placeholder="usuario@correo.com o +52..." className="w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-4 py-2 rounded-lg border border-[var(--v-border-light)]" />
                  <p className="mt-1 text-xs text-[var(--v-text-tertiary)]">El usuario ya debe tener una cuenta en Vecindus. Si no la tiene, usa "Nuevo usuario".</p>
                </div>
              )}
              <div>
                <label className="block text-[var(--v-text-secondary)] text-sm mb-1">Rol</label>
                {/* (2026-09-07) Residente adders cannot create propietarios
                    (backend POST /units/:id/residents rejects it), so hide the
                    option instead of letting them hit the 403. */}
                <select name="role" defaultValue="resident" className="w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-4 py-2 rounded-lg border border-[var(--v-border-light)]">
                  <option value="resident">Residente</option>
                  <option value="personal">Personal</option>
                  {actorCanChangeRoles && <option value="propietario">Propietario</option>}
                </select>
              </div>
              {condoUnits.length > 0 && (
                <div>
                  <label className="block text-[var(--v-text-secondary)] text-sm mb-1">Unidad</label>
                  <select name="unitId" value={residenteUnitId} onChange={(e) => setResidenteUnitId(e.target.value)} className="w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-4 py-2 rounded-lg border border-[var(--v-border-light)]">
                    <option value="">Selecciona unidad</option>
                    {condoUnits.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}
                  </select>
                </div>
              )}
              <div className="p-4 border-t border-[var(--v-border-light)] flex gap-3">
                <button type="button" onClick={() => setShowAddResidenteModal(false)} className="flex-1 py-2 rounded-lg text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]">Cancelar</button>
                <button type="submit" className="flex-1 bg-[var(--v-teal-400)] hover:bg-[var(--v-teal-500)] text-white py-2 rounded-lg font-medium transition-colors">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Editar residente — FINAL: PATCH /condos/:condoId/members/:userId { name, role }. Email no editable aquí (solo super-admin). */}
      {editPerson && (() => {
        const active = contexts.find(c => c.id === activeContextId);
        const managed = contexts.find(c => c.kind === 'condo');
        const condoId = active?.condoId || managed?.condoId || user?.condoId;
        // UNIFIED (2026-09-07, Jaime ruling): ONE rule set for standalone AND
        // condo-linked units — no context-type special-casing in the UI. When
        // the target membership row has a unitId, ALWAYS edit via the
        // unit-keyed endpoint PATCH /units/:unitId/residents/:userId
        // (unitsAPI.updateUnitResident); the backend unit routes authorize
        // owner/propietario/condo-admin/residente-actor uniformly for both
        // unit types. The condo-keyed PATCH /condos/:condoId/members/:userId
        // path remains ONLY as the legacy fallback for rows without a unit
        // anchor (old condo memberships with unitId === null).
        const editUnitScoped = Boolean(editPerson.unitId);
        return (
          <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
            <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] w-full max-w-md p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-[var(--v-text-primary)]">Editar residente</h3>
                <button aria-label="Cerrar" onClick={() => setEditPerson(null)}><X className="w-5 h-5" /></button>
              </div>
              <form className="space-y-4" onSubmit={async (e) => {
                e.preventDefault();
                const fd = new FormData(e.target as HTMLFormElement);
                if (!editPerson.userId || (!editUnitScoped && !condoId)) { setError('No se pudo identificar al residente a editar.'); return; }
                // 2026-09-07 (peer review): contract split — the unit-keyed
                // endpoint PATCH /units/:unitId/residents/:userId is
                // PERSON-FIELDS-ONLY (name/phone/bio); the backend now
                // explicitly rejects `role` and other privileged fields with
                // 400 PRIVILEGED_FIELDS_NOT_ALLOWED. Role changes at a
                // standalone unit are ownership-transfer territory
                // (OWNERSHIP_TRANSFER_REQUIRED) and handled at unit edit —
                // so the unit-scoped payload never sends `role`. The
                // condo-keyed PATCH /condos/:id/members path keeps its
                // existing { name, role } contract.
                const editPayload: Record<string, string> = {
                  name: String(fd.get('name') || '').trim(),
                };
                // 2026-09-07 (Jaime ruling): resident actors never send `role`
                // — the backend rejects privileged fields from them.
                if (!editUnitScoped && actorCanChangeRoles) editPayload.role = String(fd.get('role') || 'resident');
                try {
                  if (editUnitScoped) await unitsAPI.updateUnitResident(String(editPerson.unitId), editPerson.userId, editPayload);
                  else await condosAPI.updatePerson(condoId as string, editPerson.userId, editPayload);
                  setEditPerson(null);
                  setNotice('Cambios guardados');
                  await loadStaff();
                } catch (err: any) {
                  if (!isPlanLimitError(err)) {
                    setError(apiErrorMessage(err, 'Error al actualizar el residente'));
                  }
                }
              }}>
                <label className="block text-sm text-[var(--v-text-secondary)]">Nombre completo
                  <input name="name" required defaultValue={editPerson.fullName} className="mt-1 w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-3 py-2 rounded-lg border border-[var(--v-border-light)]" />
                </label>
                <label className="block text-sm text-[var(--v-text-secondary)]">Email (no editable — solo administración general)
                  <input type="email" value={editPerson.email || ''} readOnly disabled className="mt-1 w-full bg-[var(--v-surface-2)] text-[var(--v-text-secondary)] px-3 py-2 rounded-lg border border-[var(--v-border-light)] opacity-70 cursor-not-allowed" />
                </label>
                <label className="block text-sm text-[var(--v-text-secondary)]">Rol
                  {editUnitScoped ? (
                    // Unit-scoped edit cannot change roles (ownership transfer
                    // happens at unit edit); select disabled so the UI matches
                    // the backend person-fields-only contract.
                    <>
                      <select name="role" disabled defaultValue={editPerson.roleType === 'propietario' ? 'propietario' : 'resident'} className="mt-1 w-full bg-[var(--v-surface-2)] text-[var(--v-text-secondary)] px-3 py-2 rounded-lg border border-[var(--v-border-light)] opacity-70 cursor-not-allowed">
                        <option value="resident">Residente</option>
                        <option value="propietario">Propietario</option>
                      </select>
                      <span className="mt-1 block text-xs text-[var(--v-text-secondary)]">El rol no se puede cambiar desde aquí. La titularidad se transfiere desde la edición de la unidad.</span>
                    </>
                  ) : actorCanChangeRoles ? (
                    <select name="role" defaultValue={editPerson.roleType === 'propietario' ? 'propietario' : 'resident'} className="mt-1 w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-3 py-2 rounded-lg border border-[var(--v-border-light)]">
                      <option value="resident">Residente</option>
                      <option value="propietario">Propietario</option>
                    </select>
                  ) : (
                    // Resident actors: role changes are rejected by the backend,
                    // so the select stays disabled to match the contract.
                    <>
                      <select name="role" disabled defaultValue={editPerson.roleType === 'propietario' ? 'propietario' : 'resident'} className="mt-1 w-full bg-[var(--v-surface-2)] text-[var(--v-text-secondary)] px-3 py-2 rounded-lg border border-[var(--v-border-light)] opacity-70 cursor-not-allowed">
                        <option value="resident">Residente</option>
                        <option value="propietario">Propietario</option>
                      </select>
                      <span className="mt-1 block text-xs text-[var(--v-text-secondary)]">El rol no se puede cambiar desde tu acceso.</span>
                    </>
                  )}
                </label>
                {editPerson.passwordSet !== true && (
                  <div className="rounded-lg border border-[var(--v-border-light)] bg-[var(--v-surface-2)] p-3">
                    <p className="text-xs text-[var(--v-text-secondary)] mb-2">
                      {editPerson.passwordSet === false
                        ? 'Este residente aún no ha creado su contraseña. Envíale el enlace de invitación.'
                        : 'Si este residente aún no ha creado su contraseña, puedes reenviarle el enlace de invitación.'}
                    </p>
                    <button
                      type="button"
                      onClick={async () => { const url = await resendInvite(editPerson.userId); if (url) setEditPerson(null); }}
                      className="w-full py-2 rounded-lg border border-[var(--v-teal-400)] text-[var(--v-teal-400)] text-sm font-medium hover:bg-[var(--v-teal-400)]/10 transition-colors"
                    >
                      Reenviar enlace de invitación
                    </button>
                  </div>
                )}
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setEditPerson(null)} className="flex-1 py-2 rounded-lg text-[var(--v-text-secondary)]">Cancelar</button>
                  <button type="submit" className="flex-1 py-2 rounded-lg bg-[var(--v-teal-400)] text-white font-medium">Guardar</button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      {/* Transferir propiedad — FINAL: POST /units/:unitId/transfer-ownership { email } */}
      {transferUnit && (() => {
        return (
          <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
            <div className="bg-[var(--v-surface-1)] rounded-xl border border-amber-400/40 w-full max-w-md p-6 shadow-2xl">
              <h3 className="text-xl font-semibold text-[var(--v-text-primary)]">Transferir propiedad</h3>
              <p className="text-sm text-[var(--v-text-secondary)] mt-2">
                Vas a transferir la propiedad de <strong className="text-[var(--v-text-primary)]">{transferUnit.unitLabel || 'la unidad'}</strong> a otro usuario.
                Perderás el rol de propietario de esta unidad y el nuevo dueño tendrá control total sobre ella. Esta acción no se puede deshacer.
              </p>
              <form className="space-y-4 mt-4" onSubmit={async (e) => {
                e.preventDefault();
                const fd = new FormData(e.target as HTMLFormElement);
                if (!transferUnit.unitId) { setError('No se encontró la unidad a transferir.'); return; }
                try {
                  await condosAPI.transferOwnership(transferUnit.unitId, String(fd.get('targetEmail') || '').trim());
                  setTransferUnit(null);
                  setNotice('Propiedad transferida');
                  await loadStaff();
                } catch (err: any) {
                  if (!isPlanLimitError(err)) {
                    setError(apiErrorMessage(err, 'Error al transferir la propiedad'));
                  }
                }
              }}>
                <label className="block text-sm text-[var(--v-text-secondary)]">Email del nuevo propietario
                  <input name="targetEmail" type="email" required placeholder="usuario@correo.com" className="mt-1 w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-3 py-2 rounded-lg border border-[var(--v-border-light)]" />
                </label>
                <label className="flex items-start gap-2 text-sm text-[var(--v-text-secondary)]">
                  <input type="checkbox" name="confirm" required className="mt-0.5 accent-[var(--v-teal-400)]" />
                  <span>Entiendo que la propiedad y el control de la unidad se moverán a este usuario.</span>
                </label>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setTransferUnit(null)} className="flex-1 py-2 rounded-lg text-[var(--v-text-secondary)]">Cancelar</button>
                  <button type="submit" className="flex-1 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-medium">Transferir</button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
